import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseDeleteTeamUserActionProps = {
    user: CompanyUser;
    teamId: string;
};

type UseDeleteTeamUserActionData = {
    /** Whether this viewer can take the member out of this team. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    deleteTeamUser: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Removing a member from one team — not from the company, which is
 * `useDeleteCompanyUserAction`. Whether this viewer may do it, why not when
 * they may not, and the call itself.
 */
export function useDeleteTeamUserAction(
    props: UseDeleteTeamUserActionProps
): UseDeleteTeamUserActionData {
    const { user, teamId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // Team capabilities hang off the member's entry for that team.
    const canRemove = user.teams.find((team) => team.teamId === teamId)
        ?.capabilities.canRemove;
    const [isAllowed, reason] = useMemo(() => {
        // No entry means the member is not in this team at all, so there is
        // nothing to remove them from.
        if (!canRemove) {
            return [false, t('actions.remove_from_team.reason_not_in_team')];
        }

        // The API decides who is permitted to remove members from this team,
        // and hands back a ready-to-show reason when they are not.
        if (!canRemove.allowed) {
            return [
                false,
                canRemove.reason ??
                    t('actions.remove_from_team.reason_not_permitted')
            ];
        }

        return [true, ''];
    }, [canRemove, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `DELETE /v3/users/{id}/teams/{teamId}`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'CANNOT_REMOVE_USER_WITH_OPEN_TASKS')) {
                // Only shows up once the removal is attempted — the capability
                // cannot predict it.
                return t('actions.remove_from_team.error_open_tasks');
            }
            if (isApiError(error, 'USER_NOT_IN_TEAM')) {
                return t('actions.remove_from_team.error_not_in_team');
            }
            if (isApiError(error, 'TEAM_NOT_FOUND')) {
                return t('actions.remove_from_team.error_no_team');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.remove_from_team.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.users
                .removeUserFromTeam(user.id, teamId)
                .then((response) => response.data.data.user),
        onSuccess: (updated) => {
            addOrUpdateCompanyUsersCache(queryClient, updated);

            UIToast.success(t('actions.remove_from_team.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        deleteTeamUser: () => mutation.mutateAsync().then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
